// Repair combos: a run of neighbouring blocks repaired in quick succession
// scores as one (Unity `BlockCombos.cs`, `unity-overview.md` §3 "Blocks,
// infection, repair" and §5 "Score multipliers"). Pure logic: no WebGL, no DOM
// (00-architecture.md §1).

import type { EventBus } from "./events";
import type { Cell } from "./blocks";
import { COMBO_SCORES, COMBO_WINDOW } from "./defs";

/** True when the two cells share a face — one of the 6 axis directions. */
function isNeighbour(a: Cell, b: Cell): boolean {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]) === 1;
}

/**
 * The running repair chain. A repair extends it when the cell is an axis
 * neighbour of the previous one and lands within `COMBO_WINDOW`; anything else
 * breaks the chain, which scores it if it reached length 2 and starts a new one
 * at the breaking cell.
 *
 * DEVIATION from `BlockCombos.AddBlock`, as plan 08 specifies: the original
 * *also* breaks the chain when the direction changes between two steps (its
 * `lastDir != newDir` branch), so only straight lines score there. The plan
 * calls for adjacency + timing alone, which lets an L-shaped run score — a
 * strictly more forgiving rule. The original further requires the block one
 * step further along to be non-ghost before it will build the list at all; that
 * gate has no meaning here, because ghosts are derived rather than stored
 * (`blocks.ts`). Its `PrepareComboList` also appends without clearing, so a
 * chain there accumulates entries across steps and can overshoot its own
 * reward table — plainly a bug, and not reproduced.
 */
export class ComboTracker {
  private chain: Cell[] = [];
  private lastTime = 0;

  constructor(private readonly events: EventBus) {}

  /** Record a repair that actually changed a block, at simulation time `simTime`. */
  addRepair(cell: Cell, simTime: number): void {
    const last = this.chain.length > 0 ? this.chain[this.chain.length - 1] : null;
    const extended =
      last !== null && isNeighbour(last, cell) && simTime - this.lastTime <= COMBO_WINDOW;
    if (extended) {
      this.chain.push(cell);
    } else {
      this.endChain();
      this.chain = [cell];
    }
    this.lastTime = simTime;
  }

  /** End the chain, scoring it if it got long enough. */
  private endChain(): void {
    const cells = this.chain;
    this.chain = [];
    if (cells.length < 2) return;
    this.events.emit("combo", { cells, length: cells.length });
    // Clamped rather than dropped: the table stops at a chain of 7, and a
    // longer one is worth at least as much as the longest listed.
    const index = Math.min(cells.length - 2, COMBO_SCORES.length - 1);
    this.events.emit("scored", { points: COMBO_SCORES[index], multiplier: 1 });
  }
}
